/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sun, Moon, Monitor, Crown, DatabaseZap, Loader2, Bell, BellOff, Percent, ArrowLeft, History, RefreshCw, Wine, Trash2, TrendingUp, Users, Download, HelpCircle, Upload, Activity, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import { usePremium } from "@/context/PremiumContext";
import { useAuth, useTier } from "@/context/AuthContext";
import { apiClient, getAdminLastUsedMap } from "@/lib/api";
import { useCollections } from "@/context/CollectionContext";
import { resolveBackHref } from "@/lib/navTracker";
import {
  getCleanupHistoryCap,
  setCleanupHistoryCap,
  CLEANUP_HISTORY_CAP_CHOICES,
} from "@/lib/cleanupHistory";
import { loadMigrationPlan, runMigration } from "@/lib/templateMigration";
import { importSampleData, removeSampleData, reconcileSampleCollection, hasSampleData, SAMPLE_MANIFEST } from "@/lib/sampleImport";
import { getRevenueCounters } from "@/lib/revenueCounters";
import { useCleanupDigest, clearCleanupDigest } from "@/lib/cleanupDigest";

/* -------------------------------------------------------------------------
 * Static ISO 4217 subset — most-used currencies for the app's audience.
 * The list is intentionally small and category-agnostic. To extend, append
 * `{ code, name, symbol }` entries — no logic depends on the array length.
 * ------------------------------------------------------------------------- */
const ISO_CURRENCIES = [
  { code: "NZD", name: "New Zealand Dollar", symbol: "$" },
  { code: "AUD", name: "Australian Dollar",  symbol: "$" },
  { code: "USD", name: "US Dollar",          symbol: "$" },
  { code: "CAD", name: "Canadian Dollar",    symbol: "$" },
  { code: "GBP", name: "British Pound",      symbol: "£" },
  { code: "EUR", name: "Euro",               symbol: "€" },
  { code: "SGD", name: "Singapore Dollar",   symbol: "$" },
  { code: "HKD", name: "Hong Kong Dollar",   symbol: "$" },
  { code: "ZAR", name: "South African Rand", symbol: "R" },
  { code: "ZWL", name: "Zimbabwe Dollar",    symbol: "Z$" },
  { code: "INR", name: "Indian Rupee",       symbol: "₹" },
  { code: "JPY", name: "Japanese Yen",       symbol: "¥" },
  { code: "CHF", name: "Swiss Franc",        symbol: "CHF" },
  { code: "NOK", name: "Norwegian Krone",    symbol: "kr" },
  { code: "SEK", name: "Swedish Krona",      symbol: "kr" },
  { code: "DKK", name: "Danish Krone",       symbol: "kr" },
  { code: "BRL", name: "Brazilian Real",     symbol: "R$" },
  { code: "MXN", name: "Mexican Peso",       symbol: "$" },
  { code: "AED", name: "UAE Dirham",         symbol: "د.إ" },
  { code: "SAR", name: "Saudi Riyal",        symbol: "﷼" },
  { code: "TRY", name: "Turkish Lira",       symbol: "₺" },
];

export default function Settings() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { canPremiumUI, canCsvExport, canImport, isAdmin } = useTier();
  const backHref = resolveBackHref({
    stateFrom: location.state?.from,
    currentPath: location.pathname + location.search,
    fallback: "/",
  });
  const { theme, setTheme } = useTheme();
  const { premium, setPremium } = usePremium();
  const { collections, refresh: refreshCollections } = useCollections();
  const [edits, setEdits] = useState({});
  // Global account-level settings (2026-02) — replaces per-collection
  // currency editing. Stored in localStorage so it survives reloads
  // without touching backend schema. `globalCurrency` is an ISO code
  // (matched against ISO_CURRENCIES). `globalTaxPercent` is a numeric
  // string 0..30 that will be consumed later by inclusive/exclusive
  // pricing math (not implemented in this task).
  const [globalCurrency, setGlobalCurrency] = useState(() => {
    try {
      return localStorage.getItem("cdi.settings.currency") || "NZD";
    } catch (_e) { return "NZD"; }
  });
  const [globalTaxPercent, setGlobalTaxPercent] = useState(() => {
    try {
      return localStorage.getItem("cdi.settings.taxPercent") ?? "0";
    } catch (_e) { return "0"; }
  });
  const [savingGlobalSettings, setSavingGlobalSettings] = useState(false);
  // Backend hydrate (2026-02-25) — Settings persistence layer. On mount,
  // fetch the caller's stored settings and mirror them into both React
  // state and the existing localStorage keys so every other surface
  // (Analytics / ItemEntry / ItemDetail / RecordSaleModal / itemValue)
  // keeps working without any code changes. Fails silently (falls back
  // to whatever localStorage already had) if the backend is unreachable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get("/settings/global");
        if (cancelled || !data) return;
        const code = String(data.currency || "").toUpperCase();
        const pct = data.tax_percent;
        if (code) {
          setGlobalCurrency(code);
          try { localStorage.setItem("cdi.settings.currency", code); } catch {}
        }
        if (pct !== null && pct !== undefined && !Number.isNaN(Number(pct))) {
          setGlobalTaxPercent(String(pct));
          try { localStorage.setItem("cdi.settings.taxPercent", String(pct)); } catch {}
        }
      } catch { /* offline / not-signed-in — keep localStorage values */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const saveGlobalSettings = async () => {
    const code = String(globalCurrency || "").toUpperCase();
    const iso = ISO_CURRENCIES.find((x) => x.code === code);
    if (!iso) { toast.error("Please choose a valid currency."); return; }
    const raw = String(globalTaxPercent ?? "").trim();
    const pct = Number(raw);
    if (raw === "" || Number.isNaN(pct) || pct < 0 || pct > 30) {
      toast.error("Tax percentage must be a number between 0 and 30.");
      return;
    }
    setSavingGlobalSettings(true);
    try {
      // Backend first so downstream consumers pick up the canonical
      // value on next reload; then mirror into localStorage so the
      // same-tab consumers (which read localStorage synchronously) see
      // the new value immediately.
      await apiClient.put("/settings/global", { currency: code, tax_percent: pct });
      localStorage.setItem("cdi.settings.currency", code);
      localStorage.setItem("cdi.settings.taxPercent", String(pct));
      toast.success("Settings saved.");
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || err?.response?.data?.detail || err?.message || "Could not save settings.";
      toast.error(typeof msg === "string" ? msg : "Could not save settings.");
    } finally {
      setSavingGlobalSettings(false);
    }
  };
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  // Notifications preference — purely client-side (localStorage). Drives any
  // in-app toast / banner gating we add later. Default ON.
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    try {
      const v = localStorage.getItem("cdi.notifications");
      return v === null ? true : v === "true";
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("cdi.notifications", String(notificationsEnabled)); }
    catch { /* noop */ }
  }, [notificationsEnabled]);

  // Default Pricing Mode — purely client-side (localStorage). New items
  // pre-fill their per-item pricing-mode toggle from this preference.
  // Values: "inc" = Enter prices including GST, "ex" = Enter prices
  // excluding GST. Default "ex" matches the historical app behaviour.
  const [defaultPricingMode, setDefaultPricingMode] = useState(() => {
    try {
      const v = localStorage.getItem("cdi.defaultPricingMode");
      return v === "inc" ? "inc" : "ex";
    } catch { return "ex"; }
  });
  useEffect(() => {
    try { localStorage.setItem("cdi.defaultPricingMode", defaultPricingMode); }
    catch { /* noop */ }
  }, [defaultPricingMode]);

  // Cleanup History cap — how many recent cleanup runs CDI keeps per
  // category in localStorage. Read via the shared helper (guards against
  // tampered values) so this control is the single source of truth for
  // both the CategoryBuilder field cleanup and the Items duplicate merge.
  const [historyCap, setHistoryCap] = useState(() => getCleanupHistoryCap());

  // Template Migration section state. `migCount` reflects the number of
  // items whose `item_data` has drifted from the current template.
  // Fetched on mount + after every Run so the count refreshes without
  // a full reload.
  const [migCount, setMigCount] = useState(null);   // null = not yet loaded
  const [migRunning, setMigRunning] = useState(false);
  const refreshMigrationCount = async () => {
    try {
      const plan = await loadMigrationPlan();
      setMigCount(plan.total);
      return plan;
    } catch {
      setMigCount(null);
      return null;
    }
  };
  useEffect(() => { refreshMigrationCount(); }, []);
  const handleRunMigration = async () => {
    setMigRunning(true);
    try {
      const plan = await loadMigrationPlan();
      if (plan.total === 0) {
        toast.success("All items already aligned with the current template.");
        setMigCount(0);
        return;
      }
      const { migrated, failed } = await runMigration(plan);
      if (failed.length === 0) {
        toast.success(`Migrated ${migrated.length} item${migrated.length === 1 ? "" : "s"}.`);
      } else {
        toast.error(`Migrated ${migrated.length}, ${failed.length} failed.`);
      }
      await refreshMigrationCount();
    } catch (e) {
      toast.error("Migration failed. Check your connection and try again.");
    } finally {
      setMigRunning(false);
    }
  };

  // Sample Import — checks localStorage AND reconciles with the backend
  // (user may have deleted the collection from the Collections page,
  // in which case the flag re-enables automatically).
  const [sampleLoaded, setSampleLoaded] = useState(() => hasSampleData());
  const [sampleBusy, setSampleBusy] = useState(false);
  // P — Cellar Export: one-click JSON download of every collection + item.
  const [exportBusy, setExportBusy] = useState(false);
  // P — Cellar Import: reverse of export. Non-destructive: PATCH-if-exists,
  // POST-if-missing. Never deletes existing records. `importFile` holds the
  // File selected in the input; `importBusy` disables both controls while
  // the round-trip is in-flight.
  const [importFile, setImportFile] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  // Admin — Reseed Master Templates: one-click repair that re-runs the
  // Master Template seeder against every Master-Templated category. Same
  // logic as the `reseed_and_cleanup_2026_02.py` migration; safe to
  // re-run. `reseedBusy` disables the button while in-flight;
  // `reseedResult` holds the last summary so it can be rendered inline
  // beneath the row.
  const [reseedBusy, setReseedBusy] = useState(false);
  const [reseedResult, setReseedResult] = useState(null);
  // Rotate admin token — generates a fresh CDI_ADMIN_TOKEN on the
  // backend, persists both .env files, and triggers a supervisor
  // restart of both services. The response payload includes the new
  // token so the current session's axios default can be swapped in
  // before the restart lands.
  const [rotateBusy, setRotateBusy] = useState(false);

  const rotateAdminToken = async () => {
    if (rotateBusy) return;
    if (!window.confirm("Generate a new admin token? Both services will restart.")) return;
    setRotateBusy(true);
    try {
      const { data } = await apiClient.post("/admin/rotate-token");
      if (data?.new_token) {
        apiClient.defaults.headers.common["X-CDI-Admin"] = data.new_token;
      }
      toast.success("Admin token rotated. Services restarting.");
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Admin token missing or invalid. Check REACT_APP_CDI_ADMIN_TOKEN.");
      } else {
        toast.error("Rotate failed. Check your connection and try again.");
      }
    } finally {
      setRotateBusy(false);
    }
  };
  // Admin — Health Panel: pings GET /admin/health on mount. A 200 with
  // `token_valid: true` confirms the shared X-CDI-Admin secret is
  // accepted; the returned `endpoints` list reports registration + guard
  // status for every admin route. `health` is null while in-flight,
  // `{ token_valid: false }` on 403, `{ error: true }` on network fail.
  const [health, setHealth] = useState(null);
  const [healthBusy, setHealthBusy] = useState(false);
  // Last-used map read from localStorage. Refreshed alongside the health
  // fetch so the panel picks up admin calls made during this session
  // without a page reload.
  const [lastUsed, setLastUsed] = useState(() => getAdminLastUsedMap());

  const refreshAdminHealth = async () => {
    if (!isAdmin) return;
    setHealthBusy(true);
    try {
      const { data } = await apiClient.get("/admin/health");
      setHealth(data);
    } catch (e) {
      if (e?.response?.status === 403) {
        setHealth({ token_valid: false, endpoints: [] });
      } else {
        setHealth({ error: true, endpoints: [] });
      }
    } finally {
      setLastUsed(getAdminLastUsedMap());
      setHealthBusy(false);
    }
  };

  useEffect(() => { refreshAdminHealth(); }, [isAdmin]);

  // Prune Cold — soft-hides a Cold admin endpoint from the Health panel
  // by stashing `{prunedAt, note}` per METHOD::/api/template key into
  // localStorage. Purely a UI signal; the endpoint keeps working and
  // stays on the backend. `showPruned` toggles the hidden rows back on.
  const PRUNE_KEY = "cdi.admin.prunedCold";
  const readPrunedMap = () => {
    try {
      const raw = localStorage.getItem(PRUNE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  };
  const writePrunedMap = (m) => {
    try { localStorage.setItem(PRUNE_KEY, JSON.stringify(m)); } catch { /* noop */ }
  };
  const [pruned, setPruned] = useState(() => readPrunedMap());
  const [showPruned, setShowPruned] = useState(false);

  const pruneEndpoint = (method, path) => {
    const note = (window.prompt("Reason for pruning this endpoint (optional):") || "").trim();
    const next = { ...pruned, [`${method}::${path}`]: { prunedAt: new Date().toISOString(), note } };
    writePrunedMap(next);
    setPruned(next);
    toast.success("Endpoint hidden from Health panel.");
  };
  const unpruneEndpoint = (method, path) => {
    const key = `${method}::${path}`;
    if (!(key in pruned)) return;
    const next = { ...pruned };
    delete next[key];
    writePrunedMap(next);
    setPruned(next);
    toast.success("Endpoint restored.");
  };

  const runReseedMasterTemplates = async () => {
    if (reseedBusy) return;
    setReseedBusy(true);
    try {
      const { data } = await apiClient.post("/admin/reseed-master-templates");
      setReseedResult(data);
      toast.success(
        `Reseeded ${data.categories_reseeded} of ${data.categories_scanned} categories.`
      );
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Admin token missing or invalid. Check REACT_APP_CDI_ADMIN_TOKEN.");
      } else {
        toast.error("Reseed failed. Check your connection and try again.");
      }
    } finally {
      setReseedBusy(false);
    }
  };

  const importCellarJSON = async () => {
    if (!importFile || importBusy) return;
    setImportBusy(true);
    let text;
    try {
      text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(reader.error || new Error("read failed"));
        reader.readAsText(importFile);
      });
    } catch {
      toast.error("Couldn't read the file.");
      setImportBusy(false);
      return;
    }
    let payload;
    try {
      payload = JSON.parse(text);
    } catch {
      toast.error("That's not a valid cellar JSON file.");
      setImportBusy(false);
      return;
    }
    const cols = Array.isArray(payload?.collections) ? payload.collections : null;
    const items = Array.isArray(payload?.items) ? payload.items : null;
    if (!cols || !items) {
      toast.error("Missing 'collections' or 'items' array in the file.");
      setImportBusy(false);
      return;
    }

    // Round-trip pattern (non-destructive):
    //   • Existing id → PATCH — updates the record in place.
    //   • Missing id (404) → POST — creates a new record (server assigns a
    //     fresh id; existing records untouched).
    // Every failure is caught + tallied; the toast reports counts either way.
    let colsUpdated = 0, colsCreated = 0, colsFailed = 0;
    for (const c of cols) {
      const id = c && c.id;
      try {
        if (id) {
          try {
            await apiClient.patch(`/collections/${id}`, c);
            colsUpdated += 1;
            continue;
          } catch (err) {
            if (err?.response?.status !== 404) throw err;
          }
        }
        await apiClient.post("/collections", c);
        colsCreated += 1;
      } catch { colsFailed += 1; }
    }

    let itemsUpdated = 0, itemsCreated = 0, itemsFailed = 0;
    for (const it of items) {
      const id = it && it.id;
      try {
        if (id) {
          try {
            await apiClient.patch(`/items/${id}`, it);
            itemsUpdated += 1;
            continue;
          } catch (err) {
            if (err?.response?.status !== 404) throw err;
          }
        }
        await apiClient.post("/items", it);
        itemsCreated += 1;
      } catch { itemsFailed += 1; }
    }

    const summary = `Imported ${cols.length} collection${cols.length === 1 ? "" : "s"} (${colsCreated} new, ${colsUpdated} updated${colsFailed ? `, ${colsFailed} failed` : ""}) and ${items.length} item${items.length === 1 ? "" : "s"} (${itemsCreated} new, ${itemsUpdated} updated${itemsFailed ? `, ${itemsFailed} failed` : ""}).`;
    if (colsFailed || itemsFailed) toast.warning(summary);
    else toast.success(summary);
    setImportFile(null);
    setImportBusy(false);
  };

  const exportCellarJSON = async () => {
    if (exportBusy) return;
    setExportBusy(true);
    try {
      // Fetch both endpoints in parallel — read-only, no mutations. Large
      // cellars are fine: backend `to_list(20000)` covers ≥ any realistic
      // personal cellar. If a user ever exceeds that we'll paginate later.
      const [colsRes, itemsRes] = await Promise.all([
        apiClient.get("/collections"),
        apiClient.get("/items"),
      ]);
      const payload = {
        exported_at: new Date().toISOString(),
        collections: colsRes.data || [],
        items: itemsRes.data || [],
      };
      const json = JSON.stringify(payload, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `cellar-export-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(
        `Exported ${payload.collections.length} collection${payload.collections.length === 1 ? "" : "s"} · ${payload.items.length} item${payload.items.length === 1 ? "" : "s"}.`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed.");
    } finally {
      setExportBusy(false);
    }
  };

  // Cleanup Digest — session-only audit trail of housekeeping actions.
  // The hook re-renders this component whenever `addCleanupEvent` or
  // `clearCleanupDigest` fires anywhere in the app.
  const digest = useCleanupDigest();
  useEffect(() => {
    reconcileSampleCollection().then((id) => setSampleLoaded(!!id));
  }, []);
  const handleImportSample = async () => {
    setSampleBusy(true);
    try {
      const res = await importSampleData();
      if (res.alreadyExists) {
        toast("Sample collection already imported.", { icon: "🍇" });
      } else {
        toast.success("Sample collection added.");
      }
      setSampleLoaded(true);
      // Refresh the shared collections context so BottomNav + tiles
      // update immediately.
      if (typeof refreshCollections === "function") await refreshCollections();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't import sample collection.");
    } finally {
      setSampleBusy(false);
    }
  };
  const handleRemoveSample = async () => {
    setSampleBusy(true);
    try {
      await removeSampleData();
      setSampleLoaded(false);
      toast.success("Sample collection removed.");
      if (typeof refreshCollections === "function") await refreshCollections();
    } catch (e) {
      toast.error("Couldn't remove sample collection.");
    } finally {
      setSampleBusy(false);
    }
  };

  useEffect(() => {
    const map = {};
    for (const c of collections) {
      map[c.id] = { currency: c.currency, currency_symbol: c.currency_symbol };
    }
    setEdits(map);
  }, [collections]);

  const saveCollection = async (id) => {
    try {
      await apiClient.put(`/collections/${id}`, edits[id]);
      toast.success("Saved.");
      refreshCollections();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  const runLegacyMigration = async () => {
    if (!window.confirm(
      "This will copy any non-empty legacy fields (Brand, Model, Year, Condition, Purchase Price, Estimated Value, GST) "
      + "into each item's dynamic item_data JSON. Legacy columns will NOT be modified. Existing item_data values are preserved. "
      + "\n\nContinue?"
    )) return;
    setMigrating(true);
    setMigrationResult(null);
    try {
      const { data } = await apiClient.post("/items/migrate-legacy");
      setMigrationResult(data);
      toast.success(`Migration complete — ${data.migrated} item${data.migrated === 1 ? "" : "s"} updated (${data.fields_copied} field${data.fields_copied === 1 ? "" : "s"} copied).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed.");
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10" data-testid="settings-page">
      <button
        type="button"
        onClick={() => navigate(backHref)}
        data-testid="settings-back"
        aria-label="Back to previous screen"
        className="vp-btn vp-btn-secondary vp-btn-sm mb-4 inline-flex items-center gap-2"
      >
        <ArrowLeft size={14} />
        <span>Back</span>
      </button>
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 flex-wrap text-xs mb-4" data-testid="st-breadcrumb">
        <Link
          to="/"
          data-testid="st-breadcrumb-home"
          className="text-[10px] uppercase tracking-wider font-medium vp-muted px-2 py-0.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] transition-colors duration-fast"
        >
          Home
        </Link>
        <span className="vp-muted">→</span>
        <span data-testid="st-breadcrumb-current" className="text-xs font-medium">Settings</span>
      </nav>
      <div className="vp-label mb-2">Settings</div>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-8">App Preferences</h1>

      <section className="vp-card p-6 mb-6">
        <h2 className="text-lg font-semibold mb-1">Appearance</h2>
        <p className="text-xs vp-muted mb-4">Pick your default theme. CDI Collectables follows your OS preference by default.</p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { v: "system", label: "System", Icon: Monitor, sw: ["#F7F7F7", "#1A1A1A"] },
            { v: "light",  label: "Light",  Icon: Sun,     sw: ["#F7F7F7"] },
            { v: "dark",   label: "Dark",   Icon: Moon,    sw: ["#1A1A1A"] },
          ].map(({ v, label, Icon, sw }) => {
            const active = theme === v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => setTheme(v)}
                data-testid={`settings-theme-${v}`}
                aria-pressed={active}
                className={`flex flex-col items-start gap-2 p-3 rounded-md border transition-colors duration-fast ${
                  active
                    ? "border-[hsl(var(--gold))] ring-2 ring-[hsl(var(--gold))]/40 bg-[hsl(var(--accent))]"
                    : "border-[hsl(var(--border))] hover:border-[hsl(var(--border-strong))]"
                }`}
              >
                <div className="flex items-center gap-2 w-full">
                  <Icon size={14} />
                  <span className={`text-sm ${active ? "font-semibold" : "font-medium"}`}>{label}</span>
                </div>
                <div className="flex gap-1 mt-1" aria-hidden="true">
                  {sw.map((c, i) => (
                    <span
                      key={i}
                      className="w-5 h-5 rounded-sm border border-[hsl(var(--border))]"
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {canPremiumUI && (
      <section className="vp-card p-6 mb-6" data-testid="settings-premium">
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
          <Crown size={16} className="text-[hsl(var(--gold))]" />
          Premium
        </h2>
        <p className="text-xs vp-muted mb-4">Premium unlocks advanced collection types, smart templates, and enhanced analytics. Purchase Basic to unlock Premium features.</p>
        {user?.plan === "trial" ? (
          <button
            type="button"
            data-testid="settings-premium-status"
            onClick={() => navigate("/auth/purchase-plan")}
            className="mb-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--muted))] text-[11px] uppercase tracking-wider font-semibold hover:border-[hsl(var(--gold))]"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--muted-foreground,var(--text)))]" />
            Basic Plan
          </button>
        ) : (
          <div
            data-testid="settings-premium-status"
            className="mb-4 inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--muted))] text-[11px] uppercase tracking-wider font-semibold"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--muted-foreground,var(--text)))]" />
            Basic Plan
          </div>
        )}
        {user?.plan === "trial" && (
          <small data-testid="settings-premium-basic-hint" className="block text-xs vp-muted mb-4">Buy Basic to unlock Premium.</small>
        )}
        <div className="text-xs vp-muted mb-2 font-semibold uppercase tracking-wider">Locked features</div>
        <ul className="space-y-1.5 text-sm mb-5 list-disc pl-5" data-testid="settings-premium-features">
          <li>Premium collection types (Wine, Whisky, Spirits)</li>
          <li>Smart field templates &amp; guided category presets</li>
          <li>Enhanced analytics &amp; portfolio breakdowns</li>
          <li>Unlimited items &amp; unlimited collections</li>
          <li>Export to CSV / PDF <span className="vp-muted">(coming soon)</span></li>
          <li>Shareable collection reports <span className="vp-muted">(coming soon)</span></li>
        </ul>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <span className="text-xs vp-muted">Real billing arrives with Stripe in a later release.</span>
          <button
            type="button"
            onClick={() => toast.success("Premium coming soon")}
            data-testid="settings-upgrade-premium"
            className="vp-btn vp-btn-primary vp-btn-sm"
          >
            <Crown size={14} /> Upgrade to Premium
          </button>
        </div>
      </section>
      )}

      <section className="vp-card p-6 mb-6" data-testid="settings-notifications">
        <h2 className="text-lg font-semibold mb-1">Notifications</h2>
        <p className="text-xs vp-muted mb-4">In-app alerts for valuation updates, item milestones, and confirmations.</p>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            {notificationsEnabled
              ? <Bell size={16} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" />
              : <BellOff size={16} className="vp-muted" />}
            <span className="text-sm font-medium">{notificationsEnabled ? "Enabled" : "Disabled"}</span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={notificationsEnabled}
            onClick={() => {
              const next = !notificationsEnabled;
              setNotificationsEnabled(next);
              toast.success(`Notifications ${next ? "enabled" : "disabled"}.`);
            }}
            data-testid="settings-toggle-notifications"
            className={`relative inline-flex h-6 w-11 items-center rounded-full border transition-colors duration-fast ${
              notificationsEnabled
                ? "bg-[hsl(var(--gold))] border-[hsl(var(--gold))]"
                : "bg-[hsl(var(--muted))] border-[hsl(var(--border))]"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-fast ${
                notificationsEnabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </section>

      <section className="vp-card p-6 mb-6" data-testid="settings-pricing-mode">
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
          <Percent size={16} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" />
          Default Pricing Mode
        </h2>
        <p className="text-xs vp-muted mb-4">
          How you usually enter prices for new items. You can still override this per item.
        </p>
        <div className="space-y-2" role="radiogroup" aria-label="Default pricing mode">
          {[
            { v: "inc", label: "Enter prices including GST", testid: "settings-pricing-mode-inc" },
            { v: "ex",  label: "Enter prices excluding GST", testid: "settings-pricing-mode-ex"  },
          ].map(({ v, label, testid }) => {
            const active = defaultPricingMode === v;
            return (
              <label
                key={v}
                data-testid={testid}
                className={`flex items-center gap-3 cursor-pointer rounded-md border px-3 py-2.5 transition-colors duration-fast ${
                  active
                    ? "border-[hsl(var(--gold))] bg-[hsl(var(--accent))]"
                    : "border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
                }`}
              >
                <input
                  type="radio"
                  name="default-pricing-mode"
                  value={v}
                  checked={active}
                  onChange={() => {
                    setDefaultPricingMode(v);
                    toast.success(`Default pricing mode: ${label}.`);
                  }}
                  className="accent-[hsl(var(--gold))]"
                  data-testid={`${testid}-input`}
                />
                <span className={`text-sm ${active ? "font-medium" : ""}`}>{label}</span>
              </label>
            );
          })}
        </div>
      </section>

      {isAdmin && (
      <section className="vp-card p-6 mb-6" data-testid="settings-cleanup-history">
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
          <History size={16} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" />
          Cleanup History
        </h2>
        <p className="text-xs vp-muted mb-4">
          CDI keeps your most recent cleanup runs. Increase the cap to retain a longer audit trail.
        </p>
        <div
          role="radiogroup"
          aria-label="Cleanup history retention cap"
          className="flex flex-wrap gap-2"
        >
          {CLEANUP_HISTORY_CAP_CHOICES.map((n) => {
            const active = historyCap === n;
            return (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => {
                  setCleanupHistoryCap(n);
                  setHistoryCap(n);
                  toast.success(`Cleanup history cap set to ${n} runs.`);
                }}
                data-testid={`settings-history-cap-${n}`}
                className={`min-w-[3.25rem] px-3 py-1.5 rounded-md border text-sm font-medium transition-colors duration-fast ${
                  active
                    ? "border-[hsl(var(--gold))] bg-[hsl(var(--accent))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
                    : "border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
                }`}
              >
                {n}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] vp-muted mt-3" data-testid="settings-history-cap-current">
          Keeping the last <strong className="text-[hsl(var(--text))] font-semibold">{historyCap}</strong> cleanup runs per category.
        </p>
      </section>
      )}

      {isAdmin && (
      <section className="vp-card p-6 mb-6" data-testid="settings-template-migration">
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
          <RefreshCw size={16} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" />
          Template Migration
        </h2>
        <p className="text-xs vp-muted mb-4">
          Align older items with the latest template structure. Missing
          fields get default values, renamed fields carry their value
          across, and keys no longer in the template are removed.
        </p>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm">
            {migCount === null ? (
              <span className="vp-muted" data-testid="settings-migration-count-loading">Checking items…</span>
            ) : migCount === 0 ? (
              <span className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" data-testid="settings-migration-count">
                All items aligned with the current template.
              </span>
            ) : (
              <span data-testid="settings-migration-count">
                <strong className="text-[hsl(var(--text))] font-semibold">{migCount}</strong>
                {" "}item{migCount === 1 ? "" : "s"} need migration.
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleRunMigration}
            disabled={migRunning || migCount === 0}
            data-testid="settings-run-migration"
            className={`vp-btn vp-btn-sm ${migRunning || migCount === 0 ? "vp-btn-ghost border border-[hsl(var(--border))] opacity-70 cursor-not-allowed" : "vp-btn-primary"}`}
          >
            {migRunning ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            {migRunning ? "Running…" : "Run Migration"}
          </button>
        </div>
      </section>
      )}

      {isAdmin && (
      <section className="vp-card p-6 mb-6" data-testid="settings-sample-import">
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
          <Wine size={16} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" />
          Sample Import
        </h2>
        <p className="text-xs vp-muted mb-4">
          Add a demo wine collection to explore CDI&apos;s features.
        </p>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm flex items-center gap-2" data-testid="settings-sample-status">
            {sampleLoaded ? (
              <>
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[hsl(var(--gold))]" />
                <span className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] font-semibold">Sample collection imported</span>
                <span className="vp-muted">· {SAMPLE_MANIFEST.items.length} wines</span>
              </>
            ) : (
              <>
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[hsl(var(--muted-foreground,var(--text)))]" />
                <span className="vp-muted">No sample collection imported</span>
              </>
            )}
          </div>
          <div className="flex gap-2 items-center">
            {sampleLoaded && (
              <button
                type="button"
                onClick={handleRemoveSample}
                disabled={sampleBusy}
                data-testid="settings-remove-sample"
                className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))] text-[hsl(var(--danger,#d33))]"
              >
                {sampleBusy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                Remove
              </button>
            )}
            <button
              type="button"
              onClick={handleImportSample}
              disabled={sampleBusy || sampleLoaded}
              data-testid="settings-import-sample"
              className={`vp-btn vp-btn-sm ${sampleBusy || sampleLoaded ? "vp-btn-ghost border border-[hsl(var(--border))] opacity-70 cursor-not-allowed" : "vp-btn-primary"}`}
            >
              {sampleBusy ? <Loader2 size={13} className="animate-spin" /> : <Wine size={13} />}
              {sampleBusy ? "Importing…" : "Import Sample Collection"}
            </button>
          </div>
        </div>
      </section>
      )}

      <section className="vp-card p-6 mb-6" data-testid="settings-restart-tour">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex-1 min-w-[220px]">
            <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
              <HelpCircle size={16} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" />
              Coach-Marks Tour
            </h2>
            <p className="text-xs vp-muted leading-relaxed">
              Replays the guided intro that runs the first time a new user
              opens the app. Skips automatically on the Edit Item form so
              nothing overlays the Save button.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              try {
                localStorage.removeItem("cdi:first_run_completed");
              } catch { /* private mode — dispatch will still trigger the tour in-memory */ }
              window.dispatchEvent(new CustomEvent("cdi:restart-coach-marks"));
              toast.success("Tour restarted.");
            }}
            data-testid="settings-restart-tour-btn"
            className="vp-btn vp-btn-secondary vp-btn-sm"
          >
            <HelpCircle size={14} />
            Show tour again
          </button>
        </div>
      </section>

      {canCsvExport && (
      <section className="vp-card p-6 mb-6" data-testid="settings-cellar-export">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex-1 min-w-[220px]">
            <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
              <Download size={16} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" />
              Export Cellar JSON
            </h2>
            <p className="text-xs vp-muted leading-relaxed">
              Download every collection and every item as a single JSON file —
              a complete offline backup of your cellar independent of the app
              database. Read-only, no data is modified.
            </p>
          </div>
          <button
            type="button"
            onClick={exportCellarJSON}
            disabled={exportBusy}
            data-testid="settings-cellar-export-btn"
            className="vp-btn vp-btn-primary vp-btn-sm disabled:opacity-50"
          >
            {exportBusy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {exportBusy ? "Exporting…" : "Export Cellar JSON"}
          </button>
        </div>
      </section>
      )}

      {canImport && (
      <section className="vp-card p-6 mb-6" data-testid="settings-cellar-import">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex-1 min-w-[220px]">
            <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
              <Upload size={16} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" />
              Import Cellar JSON
            </h2>
            <p className="text-xs vp-muted leading-relaxed">
              Restore a previously downloaded backup. Non-destructive:
              existing records are updated in place and missing ones are
              recreated. Nothing gets deleted.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="file"
              accept="application/json,.json"
              data-testid="settings-cellar-import-input"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              disabled={importBusy}
              className="text-xs file:mr-2 file:vp-btn file:vp-btn-ghost file:vp-btn-sm file:border file:border-[hsl(var(--border))]"
            />
            <button
              type="button"
              onClick={importCellarJSON}
              disabled={!importFile || importBusy}
              data-testid="settings-cellar-import-btn"
              className="vp-btn vp-btn-primary vp-btn-sm disabled:opacity-50"
            >
              {importBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {importBusy ? "Importing…" : "Import"}
            </button>
          </div>
        </div>
      </section>
      )}

      <section className="vp-card p-6" data-testid="settings-collections">
        <h2 className="text-lg font-semibold mb-1">Collection Currencies</h2>
        <p className="text-xs vp-muted mb-4" data-testid="settings-currency-note">
          Select your currency and enter your tax percentage.
          This will be used to calculate inclusive/exclusive tax pricing across all collections.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="settings-global-currency"
              className="text-xs font-medium vp-muted"
            >
              Currency
            </label>
            <select
              id="settings-global-currency"
              data-testid="settings-global-currency"
              value={globalCurrency}
              onChange={(e) => setGlobalCurrency(e.target.value)}
              className="vp-select !py-2 !text-sm"
              aria-label="Global currency"
            >
              {ISO_CURRENCIES.map((cc) => (
                <option key={cc.code} value={cc.code}>{cc.code} — {cc.name}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="settings-global-tax-percent"
              className="text-xs font-medium vp-muted"
            >
              Tax Percentage (%)
            </label>
            <input
              id="settings-global-tax-percent"
              data-testid="settings-global-tax-percent"
              type="number"
              min="0"
              max="30"
              step="0.01"
              value={globalTaxPercent}
              onChange={(e) => setGlobalTaxPercent(e.target.value)}
              className="vp-input !py-2 !text-sm"
              aria-label="Global tax percentage"
            />
          </div>
        </div>
        <div className="mt-4">
          <button
            type="button"
            onClick={saveGlobalSettings}
            disabled={savingGlobalSettings}
            data-testid="settings-global-save"
            className="vp-btn vp-btn-primary vp-btn-sm disabled:opacity-50"
          >
            {savingGlobalSettings ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </section>
      {isAdmin && (
      <section className="vp-card p-6 mt-6" data-testid="admin-revenue-overview">
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
          <TrendingUp size={16} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" />
          Revenue Overview
        </h2>
        <p className="text-xs vp-muted mb-5">
          Placeholder counters until Stripe billing goes live in Phase 9.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <RevenueCounterCard
            testId="admin-counter-basic-purchases"
            icon={<TrendingUp size={14} strokeWidth={2} />}
            label="Basic Purchases"
            value={getRevenueCounters().basicPurchases}
            subLabel="One-off unlocks"
          />
          <RevenueCounterCard
            testId="admin-counter-premium-subscribers"
            icon={<Users size={14} strokeWidth={2} />}
            label="Premium Subscribers"
            value={getRevenueCounters().premiumSubscribers}
            subLabel="Active subscriptions"
          />
        </div>
      </section>
      )}

      {isAdmin && (
      <section className="vp-card p-6 mt-6" data-testid="admin-cleanup-digest">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <History size={16} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" />
            Cleanup Digest
          </h2>
          <button
            type="button"
            onClick={() => {
              clearCleanupDigest();
              toast.success("Digest cleared.");
            }}
            disabled={digest.length === 0}
            data-testid="admin-cleanup-digest-clear"
            className={`vp-btn vp-btn-sm ${digest.length === 0 ? "vp-btn-ghost border border-[hsl(var(--border))] opacity-60 cursor-not-allowed" : "vp-btn-ghost border border-[hsl(var(--border))] text-[hsl(var(--danger,#d33))]"}`}
          >
            <Trash2 size={13} /> Clear Digest
          </button>
        </div>
        <p className="text-xs vp-muted mb-4">
          Session-only trail of moves, merges, deletes, and category assignments.
        </p>
        {digest.length === 0 ? (
          <div
            data-testid="admin-cleanup-digest-empty"
            className="text-sm vp-muted italic border border-dashed border-[hsl(var(--border))] rounded-md p-4 text-center"
          >
            Nothing to report yet — housekeeping actions will appear here as they happen.
          </div>
        ) : (
          <ul
            data-testid="admin-cleanup-digest-list"
            className="max-h-[22rem] overflow-y-auto divide-y divide-[hsl(var(--border))] rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg))]"
          >
            {digest.map((evt, i) => (
              <li
                key={`${evt.timestamp}-${i}`}
                data-testid={`admin-cleanup-digest-item-${i}`}
                className="px-4 py-2.5 text-sm flex items-start gap-3"
              >
                <span
                  className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border border-[hsl(var(--gold))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] shrink-0 mt-0.5"
                  data-testid={`admin-cleanup-digest-item-${i}-type`}
                >
                  {evt.type}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs vp-muted font-mono">
                    {new Date(evt.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="truncate">
                    {evt.type === "move" || evt.type === "assign" ? (
                      <>
                        {evt.type === "move" ? "Moved" : "Assigned"}{" "}
                        <strong className="font-semibold">&ldquo;{evt.itemName}&rdquo;</strong>{" "}
                        {evt.fromCategory && (<>from <span className="vp-muted">{evt.fromCategory}</span>{" "}</>)}
                        <span className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]">→</span>{" "}
                        <span className="font-semibold">{evt.toCategory || "—"}</span>
                      </>
                    ) : evt.type === "merge" ? (
                      <>
                        Merged{" "}
                        <span className="vp-muted">{evt.fromCategory}</span>{" "}
                        <span className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]">→</span>{" "}
                        <strong className="font-semibold">&ldquo;{evt.itemName}&rdquo;</strong>
                        {evt.toCategory && (<> <span className="vp-muted">({evt.toCategory})</span></>)}
                      </>
                    ) : (
                      <>Deleted category <strong className="font-semibold">&ldquo;{evt.itemName}&rdquo;</strong></>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      )}

      {isAdmin && (
      <section className="vp-card p-6 mt-6" data-testid="settings-advanced">
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
          <DatabaseZap size={16} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" />
          Advanced
        </h2>
        <p className="text-xs vp-muted mb-5">One-off data tools. Use carefully.</p>

        {/* Admin Health — pings /api/admin/health on mount and renders a
            green/red pill for every admin route. Confirms both the
            shared X-CDI-Admin token is accepted AND every route is
            still registered + guarded. Zero side effects. */}
        <div
          className="border border-[hsl(var(--border))] rounded-lg p-4 mb-4"
          data-testid="settings-admin-health"
        >
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div className="flex-1 min-w-[220px]">
              <div className="font-medium text-sm mb-1 flex items-center gap-2">
                <Activity size={14} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" />
                Admin Health
              </div>
              <p className="text-xs vp-muted leading-relaxed">
                Confirms the admin token is accepted and every admin route
                is still registered + guarded. Purely read-only — no data
                touched.
              </p>
            </div>
            <button
              type="button"
              onClick={refreshAdminHealth}
              disabled={healthBusy}
              data-testid="settings-admin-health-refresh"
              className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))] disabled:opacity-50"
            >
              {healthBusy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              Refresh
            </button>
          </div>

          {/* Auth banner */}
          <div
            data-testid="settings-admin-health-auth"
            className={`text-xs rounded-md border px-3 py-2 mb-3 flex items-center gap-2 ${
              health && health.token_valid
                ? "border-[hsl(var(--gold))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
                : health && health.error
                ? "border-[hsl(var(--loss,#d33))] text-[hsl(var(--loss,#d33))]"
                : health && health.token_valid === false
                ? "border-[hsl(var(--loss,#d33))] text-[hsl(var(--loss,#d33))]"
                : "border-[hsl(var(--border))] vp-muted"
            }`}
          >
            {health === null ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                Checking admin token…
              </>
            ) : health.error ? (
              <>
                <XCircle size={12} />
                Health check failed — backend unreachable.
              </>
            ) : health.token_valid ? (
              <>
                <CheckCircle2 size={12} />
                Admin token accepted (<code className="font-mono">X-CDI-Admin</code>).
              </>
            ) : (
              <>
                <XCircle size={12} />
                Admin token rejected — check <code className="font-mono">REACT_APP_CDI_ADMIN_TOKEN</code>.
              </>
            )}
          </div>

          {/* Per-endpoint pills. Pruned rows are filtered out by default;
              the footer "Show hidden" toggle brings them back with an
              Unprune button. */}
          {health && Array.isArray(health.endpoints) && health.endpoints.length > 0 && (() => {
            const visible = health.endpoints.filter((ep) =>
              showPruned || !pruned[`${ep.method}::${ep.path}`]
            );
            const pruneCount = health.endpoints.filter((ep) => pruned[`${ep.method}::${ep.path}`]).length;
            return (
              <>
                <ul
                  data-testid="settings-admin-health-endpoints"
                  className="divide-y divide-[hsl(var(--border))] rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg))] overflow-hidden"
                >
                  {visible.map((ep) => {
                const testKey = `${ep.method}-${ep.path}`.replace(/[^A-Za-z0-9_-]+/g, "_");
                const lastUsedIso = lastUsed[`${ep.method}::${ep.path}`];
                const pruneKey = `${ep.method}::${ep.path}`;
                const isPruned = Boolean(pruned[pruneKey]);
                // "Cold" = we have a real timestamp AND it's ≥ 30 days old.
                // Never-stamped rows are left quiet (fresh install signal,
                // not rot). 30d matches typical release/on-call cadence.
                const isCold = (() => {
                  if (!lastUsedIso) return false;
                  const t = new Date(lastUsedIso).getTime();
                  if (Number.isNaN(t)) return false;
                  return (Date.now() - t) >= 30 * 24 * 60 * 60 * 1000;
                })();
                return (
                  <li
                    key={testKey}
                    data-testid={`settings-admin-health-row-${testKey}`}
                    className={`px-3 py-2 flex flex-col text-xs ${isPruned ? "opacity-60" : ""}`}
                  >
                    <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex items-center gap-1 shrink-0 font-semibold ${
                        ep.ok
                          ? "text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
                          : "text-[hsl(var(--loss,#d33))]"
                      }`}
                    >
                      {ep.ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                    </span>
                    <span className="uppercase tracking-wider font-mono text-[10px] w-12 shrink-0 vp-muted">
                      {ep.method}
                    </span>
                    <span className="font-mono truncate flex-1">{ep.path}</span>
                    <span
                      data-testid={`settings-admin-health-lastused-${testKey}`}
                      title={lastUsedIso ? new Date(lastUsedIso).toLocaleString() : "Never called from this browser"}
                      className={`font-mono text-[10px] shrink-0 min-w-[68px] text-right ${
                        lastUsedIso ? "text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" : "vp-muted italic"
                      }`}
                    >
                      {formatLastUsed(lastUsedIso)}
                    </span>
                    {isCold && !isPruned && (
                      <span
                        data-testid={`settings-admin-health-cold-${testKey}`}
                        title="Unused for 30+ days — consider deprecating or renaming."
                        className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border border-[hsl(var(--loss,#d33))] text-[hsl(var(--loss,#d33))] shrink-0"
                      >
                        Cold
                      </span>
                    )}
                    {isCold && !isPruned && (
                      <button
                        type="button"
                        onClick={() => pruneEndpoint(ep.method, ep.path)}
                        data-testid={`settings-admin-health-prune-${testKey}`}
                        title="Hide this endpoint from the Health panel"
                        className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border border-[hsl(var(--border))] vp-muted hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] hover:border-[hsl(var(--gold))] shrink-0"
                      >
                        Prune
                      </button>
                    )}
                    {isPruned && (
                      <>
                        <span
                          data-testid={`settings-admin-health-pruned-${testKey}`}
                          title={pruned[pruneKey]?.note ? `Note: ${pruned[pruneKey].note}` : "Pruned from panel"}
                          className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border border-[hsl(var(--border))] vp-muted shrink-0"
                        >
                          Hidden
                        </span>
                        <button
                          type="button"
                          onClick={() => unpruneEndpoint(ep.method, ep.path)}
                          data-testid={`settings-admin-health-unprune-${testKey}`}
                          title="Restore this endpoint to the Health panel"
                          className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border border-[hsl(var(--border))] vp-muted hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] hover:border-[hsl(var(--gold))] shrink-0"
                        >
                          Unprune
                        </button>
                      </>
                    )}
                    <span className="flex items-center gap-2 shrink-0">
                      <span
                        className={ep.registered ? "vp-muted" : "text-[hsl(var(--loss,#d33))] font-semibold"}
                      >
                        {ep.registered ? "registered" : "missing"}
                      </span>
                      <span className="vp-muted">·</span>
                      <span
                        className={ep.guarded ? "vp-muted" : "text-[hsl(var(--loss,#d33))] font-semibold"}
                      >
                        {ep.guarded ? "guarded" : "unguarded"}
                      </span>
                    </span>
                    </div>
                    {isPruned && pruned[pruneKey]?.note && (
                      <div
                        data-testid={`settings-admin-health-note-${testKey}`}
                        className="mt-1 pl-[70px] text-[10px] italic vp-muted truncate"
                        title={pruned[pruneKey].note}
                      >
                        Note: {pruned[pruneKey].note}
                      </div>
                    )}
                  </li>
                );
              })}
                </ul>
                {pruneCount > 0 && (
                  <div
                    data-testid="settings-admin-health-pruned-footer"
                    className="mt-2 flex items-center justify-between text-[11px]"
                  >
                    <span className="vp-muted italic">
                      {pruneCount} endpoint{pruneCount === 1 ? "" : "s"} hidden.
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowPruned((v) => !v)}
                      data-testid="settings-admin-health-toggle-pruned"
                      className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
                    >
                      {showPruned ? "Hide pruned" : "Show hidden"}
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        <div className="border border-[hsl(var(--border))] rounded-lg p-4">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex-1 min-w-[220px]">
              <div className="font-medium text-sm mb-1">Migrate Legacy Data</div>
              <p className="text-xs vp-muted leading-relaxed">
                Copies Brand, Model, Year, Condition, Purchase Price, Estimated Value, and GST from each item&apos;s legacy columns
                into the new dynamic <code className="px-1 py-0.5 rounded bg-[hsl(var(--muted))] font-mono text-[11px]">item_data</code> JSON.
                Legacy columns are <strong>not</strong> deleted or modified. Existing <code className="px-1 py-0.5 rounded bg-[hsl(var(--muted))] font-mono text-[11px]">item_data</code> values are preserved.
              </p>
            </div>
            <button
              onClick={runLegacyMigration}
              disabled={migrating}
              data-testid="settings-migrate-legacy"
              className="vp-btn vp-btn-primary vp-btn-sm disabled:opacity-50"
            >
              {migrating ? <Loader2 size={14} className="animate-spin" /> : <DatabaseZap size={14} />}
              {migrating ? "Migrating…" : "Migrate Legacy Data"}
            </button>
          </div>

          {migrationResult && (
            <div data-testid="settings-migrate-result" className="mt-4 pt-4 border-t border-[hsl(var(--border))] grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <ResultTile label="Items Scanned" value={migrationResult.scanned} />
              <ResultTile label="Items Updated" value={migrationResult.migrated} accent />
              <ResultTile label="Already Current" value={migrationResult.skipped} />
              <ResultTile label="Fields Copied" value={migrationResult.fields_copied} accent />
            </div>
          )}
        </div>

        {/* P9 — Deep-link into the cross-category Rare Values Scan page
            (built in P7). Styled to match the sibling "Migrate Legacy
            Data" row above so the Advanced section stays visually
            consistent. Purely a navigation link — no data mutation, no
            auto-scan; the user clicks Run Scan on the destination page. */}
        <div className="border border-[hsl(var(--border))] rounded-lg p-4 mt-4">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex-1 min-w-[220px]">
              <div className="font-medium text-sm mb-1">Rare Values Scan</div>
              <p className="text-xs vp-muted leading-relaxed">
                Diagnostic scan across every category. Surfaces rare values,
                near-duplicate variants, and unused options grouped by
                category. Read-only — jump into a category to Merge / Delete.
              </p>
            </div>
            <Link
              to="/settings/rare-values"
              data-testid="cb-settings-rare-values-link"
              className="vp-btn vp-btn-primary vp-btn-sm"
            >
              <DatabaseZap size={14} />
              Open Rare Values Scan
            </Link>
          </div>
        </div>

        {/* Admin — Reseed Master Templates. One-click re-run of the
            unconditional-overwrite seeder against every Master-Templated
            category (wine / whisky / spirits / general / custom). Mirrors
            the `reseed_and_cleanup_2026_02.py` migration. Idempotent. */}
        <div className="border border-[hsl(var(--border))] rounded-lg p-4 mt-4">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex-1 min-w-[220px]">
              <div className="font-medium text-sm mb-1">Reseed Master Templates</div>
              <p className="text-xs vp-muted leading-relaxed">
                Re-runs the Master Template seeder on every Wine / Whisky /
                Spirits / General / Custom category. Snaps any drifted{" "}
                <code className="px-1 py-0.5 rounded bg-[hsl(var(--muted))] font-mono text-[11px]">field_type</code>,{" "}
                <code className="px-1 py-0.5 rounded bg-[hsl(var(--muted))] font-mono text-[11px]">required</code>, and{" "}
                <code className="px-1 py-0.5 rounded bg-[hsl(var(--muted))] font-mono text-[11px]">placeholder</code>{" "}
                values back to the canonical CDI schema. Custom user-added
                fields are preserved.
              </p>
            </div>
            <button
              onClick={runReseedMasterTemplates}
              disabled={reseedBusy}
              data-testid="settings-reseed-master-templates-btn"
              className="vp-btn vp-btn-primary vp-btn-sm disabled:opacity-50"
            >
              {reseedBusy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              {reseedBusy ? "Reseeding…" : "Reseed Now"}
            </button>
          </div>

          {reseedResult && (
            <div
              data-testid="settings-reseed-master-templates-result"
              className="mt-4 pt-4 border-t border-[hsl(var(--border))] grid grid-cols-3 gap-3 text-sm"
            >
              <ResultTile label="Scanned" value={reseedResult.categories_scanned} />
              <ResultTile label="Reseeded" value={reseedResult.categories_reseeded} accent />
              <ResultTile label="Skipped" value={reseedResult.categories_skipped} />
            </div>
          )}
        </div>

        {/* Admin — Rotate Admin Token. Generates a fresh
            CDI_ADMIN_TOKEN, writes it to both .env files, and restarts
            frontend + backend so the new token is baked in. */}
        <div className="border border-[hsl(var(--border))] rounded-lg p-4 mt-4">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex-1 min-w-[220px]">
              <div className="font-medium text-sm mb-1">Rotate Admin Token</div>
              <p className="text-xs vp-muted leading-relaxed">
                Generates a fresh{" "}
                <code className="px-1 py-0.5 rounded bg-[hsl(var(--muted))] font-mono text-[11px]">CDI_ADMIN_TOKEN</code>,
                updates both{" "}
                <code className="px-1 py-0.5 rounded bg-[hsl(var(--muted))] font-mono text-[11px]">.env</code>{" "}
                files, and restarts both services so the new token is picked up.
              </p>
            </div>
            <button
              onClick={rotateAdminToken}
              disabled={rotateBusy}
              data-testid="settings-rotate-admin-token-btn"
              className="vp-btn vp-btn-primary vp-btn-sm disabled:opacity-50"
            >
              {rotateBusy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              {rotateBusy ? "Rotating…" : "Rotate Now"}
            </button>
          </div>
        </div>
      </section>
      )}

      {/* -----------------------------------------------------------------
          About — static, no API calls. Version + build date come from
          Vite / CRA env vars if present; otherwise fall back to constants
          the maintainer can update by hand. Category-agnostic wording. */}
    </div>
  );
}

const ResultTile = ({ label, value, accent }) => (
  <div className="rounded-md bg-[hsl(var(--bg))] border border-[hsl(var(--border))] p-3">
    <div className="vp-label !text-[10px] mb-1">{label}</div>
    <div className={`font-mono text-lg font-semibold ${accent ? "text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" : ""}`}>{value}</div>
  </div>
);

// Compact relative-time formatter used by the Admin Health "Last used"
// column. Renders "never" when the endpoint hasn't been hit since the
// tracker was installed, otherwise a compact human string ("just now",
// "5m ago", "3h ago", "2d ago", or a locale date for anything older).
const formatLastUsed = (iso) => {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "never";
  const diff = Date.now() - t;
  if (diff < 0) return "just now";
  const s = Math.floor(diff / 1000);
  if (s < 45) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

/**
 * RevenueCounterCard — large stat tile used in the Admin Dashboard
 * → Revenue Overview grid. Dark card, gold accent on the big number,
 * icon-labelled header, small sub-label underneath. Structurally
 * identical to the existing `vp-card` pattern so it inherits both
 * light + dark palettes automatically.
 */
const RevenueCounterCard = ({ testId, icon, label, value, subLabel }) => (
  <div
    data-testid={testId}
    className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg))] p-5 flex flex-col gap-3"
  >
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider vp-muted font-semibold">
      <span className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]">{icon}</span>
      <span>{label}</span>
    </div>
    <div
      data-testid={`${testId}-value`}
      className="font-mono text-4xl sm:text-5xl font-bold tracking-tight text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] leading-none"
    >
      {value}
    </div>
    <div className="text-xs vp-muted">{subLabel}</div>
  </div>
);
